import { useState } from 'react';
import { useAlertEngine } from './alerts';
import { isOwner } from './owner';
import BacktestPanel from './components/BacktestPanel';
import DayTradePage from './components/DayTradePage';
import DetailPanel from './components/DetailPanel';
import FullScanPage from './components/FullScanPage';
import GlobalRadarPage from './components/GlobalRadarPage';
import Header from './components/Header';
import HomePage from './components/HomePage';
import MacroBar from './components/MacroBar';
import MarketOutlook from './components/MarketOutlook';
import MyStatusPage from './components/MyStatusPage';
import ScannerPage from './components/ScannerPage';
import ThemePanel from './components/ThemePanel';
import StatsPage from './components/StatsPage';

type Mode = 'home' | 'market' | 'mystatus' | 'watchlist' | 'scanner' | 'daytrade' | 'newday' | 'stats' | 'themes' | 'radar' | 'backtest';

export default function App() {
  const [mode, setMode] = useState<Mode>('home');
  const [selected, setSelected] = useState<{ ticker: string; name: string } | null>(null);
  useAlertEngine();   // 단타 알림 — 메뉴 떠나도 항상 동작(핀 보유·🔔 켠 사용자만 폴링)

  const handleSelectTicker = (ticker: string, name: string) => {
    setSelected({ ticker, name });
  };

  // 탭 전환 시 열려있던 상세 패널을 닫는다 (폰에선 패널이 전체화면이라 새 탭을 가리므로).
  const goTab = (m: Mode) => {
    setMode(m);
    setSelected(null);
  };

  return (
    // 바탕은 body의 잉크 그라데이션이 그대로 비치게 둔다(규격 V2) — 여기서 색을 덮으면 그라데이션이 죽는다.
    <div className="flex flex-col h-screen text-dc-ink1 overflow-hidden"
      style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
      {/* ↑ 아이폰 설치형 PWA(black-translucent 상태바)에서 시계·배터리가 시세바를 덮지 않게 안전영역 여백.
          안드로이드·PC는 env()가 0이라 영향 없음. */}
      <MacroBar />
      <Header mode={mode} onTab={goTab} />

      <main className="flex flex-1 min-h-0 overflow-hidden relative">
        <div
          className={`flex flex-1 min-h-0 overflow-hidden transition-all duration-300 ${
            selected && mode !== 'backtest' ? 'lg:mr-[580px]' : ''
          }`}
        >
          {mode === 'home'      && <HomePage onTab={goTab} />}
          {mode === 'market'    && <div className="flex-1 overflow-auto p-3 sm:p-4"><MarketOutlook /></div>}
          {mode === 'mystatus'  && <MyStatusPage onSelectTicker={handleSelectTicker} onTab={goTab} />}
          {mode === 'watchlist' && <ScannerPage onSelectTicker={handleSelectTicker} />}
          {mode === 'scanner'   && <FullScanPage onSelectTicker={handleSelectTicker} />}
          {mode === 'daytrade'  && <DayTradePage />}
          {/* 🗑️ 뉴단타 탭 해체(07-16) — 줍줍 board는 단타탭(옛 조종 화면)에, 성적·조합 발굴은 📊통계탭에 흡수 */}
          {mode === 'stats'     && isOwner() && <StatsPage />}    {/* 📊 자동 비교 연대기(07-10) — 관리자 전용 */}
          {mode === 'themes'    && <ThemePanel onSelectTicker={handleSelectTicker} />}
          {mode === 'radar'     && <GlobalRadarPage />}
          {mode === 'backtest'  && <BacktestPanel />}
        </div>

        {/* 슬라이드인 패널 — 폰에선 전체화면, lg+에선 580px 옆창 */}
        <div
          className="absolute top-0 right-0 h-full transition-transform duration-300 ease-in-out z-20 w-full lg:w-[580px]"
          style={{
            transform: selected ? 'translateX(0)' : 'translateX(100%)',
          }}
        >
          {selected && (
            <DetailPanel
              ticker={selected.ticker}
              name={selected.name}
              onClose={() => setSelected(null)}
            />
          )}
        </div>

        {/* 오버레이 (모바일용) */}
        {selected && (
          <div
            className="absolute inset-0 bg-black/30 z-10 lg:hidden"
            onClick={() => setSelected(null)}
          />
        )}
      </main>
    </div>
  );
}
